import { useRef, useEffect } from 'react'
import {
  faArrowsMaximize,
  faPlus,
  faRectangleHistory,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons'
import { Text } from '@/ui'
import { TypographyStyles } from '@/styles'
import { animateHeaderCompact } from './panelAnimations'
import { Leaf } from './Leaf'
import { HeaderButton } from '@/features/adviser-chat/components/HeaderButton'
import { HAVEN_TITLE } from '@/features/adviser-chat/lib/constants'

interface ChatPanelHeaderProps {
  title?: string
  compact?: boolean
  onNew?: () => void
  onHistory?: () => void
  onExpand?: () => void
  onClose?: () => void
}

export const ChatPanelHeader = ({
  title = HAVEN_TITLE,
  compact,
  onNew,
  onHistory,
  onExpand,
  onClose,
}: ChatPanelHeaderProps) => {
  const expandedRef = useRef<HTMLDivElement>(null)
  const compactRef = useRef<HTMLDivElement>(null)

  useEffect(
    () =>
      animateHeaderCompact(
        expandedRef.current,
        compactRef.current,
        Boolean(compact)
      ),
    [compact]
  )

  return (
    <div className="relative shrink-0 h-8">
      {/* Expanded: left-aligned seedling + large P22 title */}
      <div
        ref={expandedRef}
        className="absolute inset-0 flex items-center gap-3 px-6"
      >
        <Leaf size={24} />
        <Text variant={TypographyStyles.H1}>{title}</Text>
      </div>

      {/* Compact: seedling+title left, icon action buttons right */}
      {/* Initial hide via .adviser-compact-header CSS class — GSAP overrides with
          inline styles (higher specificity) so React re-renders can never fight it */}
      <div
        ref={compactRef}
        className="adviser-compact-header absolute inset-0 flex items-center justify-between pl-6 pr-3"
      >
        <div className="flex items-center gap-2">
          <Leaf size={16} />
          <Text variant={TypographyStyles.H3}>{title}</Text>
        </div>
        <div className="flex items-center">
          <HeaderButton
            icon={faPlus}
            label="New conversation"
            onClick={onNew}
          />
          <HeaderButton
            icon={faRectangleHistory}
            label="History"
            onClick={onHistory}
          />
          {onExpand && (
            <HeaderButton
              icon={faArrowsMaximize}
              label="Open full screen"
              onClick={onExpand}
            />
          )}
          <HeaderButton
            icon={faXmark}
            label="Close chat window"
            onClick={onClose}
          />
        </div>
      </div>
    </div>
  )
}
